import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Car,
  MapPin,
  Clock,
  Calendar,
  Users,
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  X,
  AlertCircle,
  MessageSquare
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-500', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: X },
  cancelled: { label: 'Cancelled', color: 'bg-slate-500', icon: AlertCircle }
};

const MyRidesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchRequests();
  }, [user, navigate]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/carpool/my-ride-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/carpool')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Carpool
        </Button>

        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-500" /> My Ride Requests
          </h1>
          <p className="text-slate-600">View your pending and approved carpool requests</p>
        </div>

        {requests.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No ride requests yet</p>
              <Link to="/carpool">
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  Browse Available Rides
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const status = statusConfig[req.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              
              return (
                <Card key={req.id} className="bg-white" data-testid={`ride-request-${req.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={`${status.color} text-white`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {new Date(req.created_at).toLocaleDateString()}
                          </span>
                        </div>

                        {req.route ? (
                          <>
                            <h3 className="font-heading font-bold text-lg">{req.route.route_name}</h3>
                            <div className="space-y-2 mt-3 text-sm text-slate-600">
                              <p className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-emerald-500" />
                                {req.route.start_location} → {req.route.end_location}
                              </p>
                              <p className="flex items-center gap-2">
                                <Clock className="w-4 h-4" /> {req.route.departure_time}
                              </p>
                              <p className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" /> {req.route.schedule_days?.join(', ')}
                              </p>
                            </div>

                            {/* Driver Info */}
                            <div className="mt-4 pt-4 border-t flex items-center gap-3">
                              <div className="w-10 h-10 bg-plp-gold rounded-full flex items-center justify-center">
                                <span className="font-bold text-plp-blue">
                                  {req.route.driver?.name?.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{req.route.driver?.name}</p>
                                <p className="text-sm text-slate-500">Driver</p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="text-slate-500">Route no longer available</p>
                        )}

                        {/* Your Pickup */}
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Your Pickup Location</p>
                          <p className="font-medium">{req.pickup_location}</p>
                        </div>

                        {/* Driver Notes */}
                        {req.driver_notes && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                            <p className="text-sm text-amber-700">
                              <MessageSquare className="w-4 h-4 inline mr-1" />
                              Driver's Note: {req.driver_notes}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex md:flex-col gap-2">
                        {req.route && req.status === 'approved' && (
                          <Link to={`/carpool/route/${req.route_id}`}>
                            <Button className="bg-emerald-600 hover:bg-emerald-700">
                              <MessageSquare className="w-4 h-4 mr-2" /> Open Chat
                            </Button>
                          </Link>
                        )}
                        {req.route && (
                          <Link to={`/carpool/route/${req.route_id}`}>
                            <Button variant="outline">View Route</Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRidesPage;
